import * as angular from "angular";
import { CampaignDataProvider } from "./campaign.dp";
import { CheckManager } from "../shared/check-manager";
import { ActivityDataProvider } from "./activity.dp";
import { Filter } from "../models/filter.model";
import { Invite } from "../models/invite.model";
import { User } from "../models/user.model";
import { PermissionCheck } from "../shared/permission";
import { ApiUserService } from "../services/user.service";
import { UserService } from "../services/users.service";
import { TeamDataProvider } from "./team.dp";
import { DialogUtils } from "../helper/dialog.helper";
import { ToastUtils } from "../helper/toast.helper";

export class UserDataProvider {
    private $rootScope: any;
    private $q: any;
    private $http: any;
    private apiUserService: ApiUserService;
    private toast: ToastUtils;
    private dialog: DialogUtils;
    private modelsUtils: any;
    private campaignDataProvider: CampaignDataProvider;
    private permissionProvider: PermissionCheck;
    private userService: UserService;
    private teamDataProvider: TeamDataProvider;
    public checkManager: CheckManager;
    private $timeout: any;
    private $cookies: any;
    private activityDataProvider: ActivityDataProvider;
    private __env: any;

    public users: any;
    public seatedUsers: any;
    public activeUser: User;
    public filteredUsers: any;
    public searchPromise: any;
    public count: any;
    public stats: any;
    public statsPromise: any;
    public seats: any;
    public seatsPromise: any;
    public filters: any;
    public loadSize: any;
    public scrollLoading: any;
    public loading: any;
    public returnPromise: any;
    public filterData: any;
    public license: any;
    public signedInUser: any;
    public userIds: any;
    public inviteIds: any;
    public message: any;
    public action: any;
    public existingUser: any;
    public error?: any;
    public countError?: any;
    public usersCount: any;
    public superUsers?: any;

    constructor(
        $rootScope: any,
        $q: any,
        $http: any,
        ApiUserService: ApiUserService,
        ToastUtils: ToastUtils,
        DialogUtils: DialogUtils,
        CampaignDataProvider: CampaignDataProvider,
        PermissionCheck: PermissionCheck,
        UserService: UserService,
        TeamDataProvider: TeamDataProvider,
        $timeout: any,
        $cookies: any,
        ActivityDataProvider: ActivityDataProvider,
        __env: any,
        ModelsUtils: any
    ) {
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.$http = $http;
        this.apiUserService = ApiUserService;
        this.toast = ToastUtils;
        this.dialog = DialogUtils;
        this.campaignDataProvider = CampaignDataProvider;
        this.permissionProvider = PermissionCheck;
        this.userService = UserService;
        this.teamDataProvider = TeamDataProvider;
        this.checkManager = new CheckManager();
        this.$timeout = $timeout;
        this.$cookies = $cookies;
        this.filters = new Filter();
        this.activityDataProvider = ActivityDataProvider;
        this.filteredUsers = [];
        this.stats = null;
        this.seats = null;
        this.scrollLoading = false;
        this.loading = true;
        this.checkManager = new CheckManager(this.filteredUsers);
        this.loadSize = 25;
        this.__env = __env;
        this.modelsUtils = ModelsUtils;

        this.users = null;
        this.seatedUsers = null;

        this.teamDataProvider.addObserver(this.resetAllUsersTeams);
    }

    getAllUsers(filters?: any, offsetValue?: any): Promise<User[]> {
        const deferred = this.$q.defer();
        if (this.users === null) {
            this.userService.all({}).then(
                (response) => {
                    let json = response["data"];
                    this.users = [];
                    for (let i = 0; i < json.data.length; i++) {
                        this.users.push(this.modelsUtils.user(json.data[i]));
                    }
                    deferred.resolve(this.users);
                },
                (error) => {
                    this.users = [];
                    deferred.reject(error);
                }
            );
        } else {
            deferred.resolve(this.users);
        }
        return deferred.promise;
    }

    setFilter(setFilters: any): Promise<User[]> {
        const deferred = this.$q.defer();
        this.filters = setFilters;
        this.count = undefined;
        this.getFilteredUsers(null, false).then(
            (users: User[]) => deferred.resolve(users),
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    resetItems(): void {
        this.setFilteredUsersCount(undefined);
        this.checkManager.checkAll(false);
        this.getFilteredUsers(null, false);
    }

    resetStats(): void {
        this.headlineStats(true);
    }

    headlineStats(reset?: boolean): Promise<any> {
        if (this.stats !== null && !reset) {
            return this.$q.resolve(this.stats);
        } else if (this.statsPromise && !reset) {
            //Has been called already and has not resolved yet.
            return this.statsPromise;
        } else {
            const deferred = this.$q.defer();

            this.userService.headlineStats({}).then(
                (response: object) => {
                    let json = response["data"];
                    this.stats = json.data;
                    deferred.resolve(this.stats);
                },
                (error: object) => {
                    this.stats = [];
                    deferred.reject(error);
                }
            );

            this.statsPromise = deferred.promise;
            return this.statsPromise;
        }
    }

    bulkDelete(users: User[]): Promise<any> {
        const deferred = this.$q.defer();
        this.userService.bulkDelete({ uids: users }).then((response) => {
            let json = response["data"];
            if (json.success) {
                this.resetItems();
                this.resetStats();
                this.checkManager.removeCheckedItems();
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    billing(users: any[], billing: any): Promise<any> {
        const deferred = this.$q.defer();

        if (billing) {
            this.userService.grant({ uids: users }).then((response: object) => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            });
        } else {
            this.userService
                .revoke({ uids: users })
                .then((response: object) => {
                    let json = response["data"];
                    if (json.success) {
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                });
        }
        return deferred.promise;
    }

    getFilteredUsers(last: any, scroll: boolean): Promise<User[]> {
        if (!last || this.filteredUsers.length < this.count) {
            this.error = false;

            let returnPromise = this.$q.defer();

            if (scroll) this.scrollLoading = true;
            else this.loading = true;

            if (this.searchPromise)
                this.searchPromise.resolve("user cancelled");
            this.searchPromise = this.$q.defer();

            let filterData = null;
            if (this.filters && this.filters.nonEmptyValues) {
                filterData = this.filters.nonEmptyValues();
                filterData.limit = this.loadSize;
                filterData.needs_teams = true;
            }

            this.$http
                .post(
                    this.__env.apiUrl() + "/campaign/user/alldash",
                    {
                        last: last ? { uid: last.uid } : null,
                        filters: filterData,
                        count: this.count,
                    },
                    {
                        timeout: this.searchPromise.promise,
                    }
                )
                .then(
                    (response: object) => {
                        let json = response["data"];

                        if (!last) {
                            this.filteredUsers.splice(
                                0,
                                this.filteredUsers.length
                            );
                        }
                        if (json.success) {
                            if (!scroll) {
                                this.getCount();
                            }

                            for (let i = 0; i < json.data.length; i++) {
                                this.filteredUsers.push(
                                    this.modelsUtils.user(json.data[i])
                                );
                            }
                            returnPromise.resolve(this.filteredUsers);
                        } else {
                            returnPromise.reject(json);
                        }

                        if (scroll) this.scrollLoading = false;
                        else this.loading = false;
                    },
                    (error: object) => {
                        if (error["status"] > -1) {
                            this.error = true;

                            if (scroll) this.scrollLoading = false;
                            else this.loading = false;
                        }
                        returnPromise.reject(error);
                    }
                );
            return returnPromise.promise;
        }
        return this.$q.resolve(this.filteredUsers);
    }

    getUsersCount(): Promise<any> {
        if (this.usersCount) {
            return this.$q.resolve(this.usersCount);
        } else {
            let deferred = this.$q.defer();
            this.userService.usersCount().then(
                (response: object) => {
                    let json = response["data"];
                    this.setUsersCount(json.data);
                    deferred.resolve(json.data);
                },
                (error: object) => {
                    this.superUsers = 0;
                    deferred.reject(error);
                }
            );
            return deferred.promise;
        }
    }

    getCount(): Promise<number> {
        this.countError = false;
        const deferred = this.$q.defer();
        let filterData = this.filters.nonEmptyValues();

        this.$http({
            url: this.__env.apiUrl() + "/campaign/user/count",
            method: "GET",
            params: {
                filters: JSON.stringify(filterData),
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                let json = response["data"];
                if (json.success) {
                    this.setFilteredUsersCount(parseInt(json.data));
                    deferred.resolve(parseInt(json.data));
                }
            },
            (error: object) => {
                if (error["status"] > -1) {
                    this.countError = true;
                }
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    updateLicenses(
        users: string[],
        invites: number[],
        seated: boolean
    ): Promise<any> {
        const deferred = this.$q.defer();

        if (seated) {
            this.userService
                .bulkActivate({ uids: users, inviteIds: invites })
                .then((response: object) => {
                    let json = response["data"];
                    if (json.success) {
                        this.resetItems();
                        this.checkManager.removeCheckedItems();
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                });
        } else {
            this.userService
                .bulkDeactivate({ uids: users, inviteIds: invites })
                .then((response: object) => {
                    let json = response["data"];
                    if (json.success) {
                        this.resetItems();
                        this.checkManager.removeCheckedItems();
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                });
        }
        return deferred.promise;
    }

    confirmActivateDialog(
        users: User[],
        invites: Invite[],
        ev: any
    ): Promise<any> {
        const deferred = this.$q.defer();
        const count = users.length + invites.length;
        const license = count > 1 ? "Licenses" : "License";

        if (count <= this.seats.available) {
            if (count) {
                this.dialog
                    .delete({
                        fullscreen: true,
                        targetEvent: ev,
                        skipHide: true,
                        locals: {
                            bodyText: `We recommend that you use locked licenses for members that will need access on an ongoing or regular basis, especially managers or staffers.`,
                            title: `Activate Locked ${license}?`,
                        },
                    })
                    .then(() => {
                        this.activateUsers(
                            users.map((user) => user.uid),
                            invites.map((invite) => invite.campaigninviteid),
                            1
                        ).then(() => deferred.resolve());
                    });
            } else {
                const error_message =
                    this.checkManager.checked_items.length !== 1
                        ? "All of these users already have a locked license"
                        : "This user already has a locked license";
                this.dialog.showPreConfiguredDialog(
                    this.dialog
                        .confirm()
                        .textContent(error_message)
                        .targetEvent(ev)
                        .ok("OK")
                );
                deferred.reject(error_message);
            }
        } else {
            this.needsPurchase(users, invites, ev, deferred);
        }
        return deferred.promise;
    }

    needsPurchase(
        users: User[],
        invites: Invite[],
        ev: any,
        promise: any
    ): void {
        let signedInUser = this.getActiveUser();
        this.dialog
            .showPreConfiguredDialog({
                template:
                    '<md-dialog aria-label="Ask For Purchase" flex="30" flex-md="60" flex-sm="80" flex-xs="95" >' +
                    '<ask-for-purchase quantity="quantity"></ask-for-purchase>' +
                    "</md-dialog>",
                targetEvent: ev,
                clickOutsideToClose: false,
                bindToController: true,
                fullscreen: true,
                multiple: true,
                scope: angular.extend(this.$rootScope.$new(true), {
                    quantity: users.length + invites.length,
                }),
            })
            .then(() => {
                if (
                    this.permissionProvider.hasStripeSubscription &&
                    !this.permissionProvider.isYearlySubscrition &&
                    this.permissionProvider.hasBillingRight()
                ) {
                    this.openPurchaseSeatsDialog(users, invites, ev, promise);
                } else {
                    let message: string;
                    if (
                        !this.permissionProvider.hasStripeSubscription ||
                        this.permissionProvider.isYearlySubscrition
                    )
                        message =
                            "Please contact our support team to request more user licenses";
                    else if (!signedInUser.billing)
                        message =
                            "You need to have billing permission to make this change";

                    this.dialog
                        .showPreConfiguredDialog(
                            this.dialog
                                .confirm()
                                .textContent(message)
                                .targetEvent(ev)
                                .ok("OK")
                        )
                        .then(() => {
                            promise.reject();
                        });
                }
            });
    }

    openPurchaseSeatsDialog(
        users: User[],
        invites: Invite[],
        event: any,
        promise: any
    ): void {
        let signedInUser = this.getActiveUser();
        if (
            this.permissionProvider.hasStripeSubscription &&
            !this.permissionProvider.isYearlySubscrition &&
            this.permissionProvider.hasBillingRight()
        ) {
            this.dialog
                .showPreConfiguredDialog({
                    template: `<md-dialog flex="100" flex-gt-xs="95" flex-gt-sm="75" flex-gt-lg="55">
                                <purchase-seats-dialog num-seats="numSeats"></purchase-seats-dialog>
                            </md-dialog>`,
                    targetEvent: event,
                    clickOutsideToClose: false,
                    bindToController: true,
                    fullscreen: true,
                    multiple: true,
                    scope: angular.extend(this.$rootScope.$new(true), {
                        numSeats: users.length + invites.length,
                    }),
                })
                .then(() => {
                    const userIds = users.map((user) => user.uid);
                    const inviteIds = invites.map((invite) => {
                        return invite.campaigninviteid;
                    });

                    this.activateUsers(userIds, inviteIds, 1).then(() => {
                        promise.resolve();
                    });
                });
        } else {
            let message: string;
            if (
                !this.permissionProvider.hasStripeSubscription ||
                this.permissionProvider.isYearlySubscrition
            )
                message =
                    "Please contact our support team to request more user licenses";
            else if (!signedInUser.billing)
                message =
                    "You need to have billing permission to make this change";

            this.dialog
                .showPreConfiguredDialog(
                    this.dialog
                        .confirm()
                        .textContent(message)
                        .targetEvent(event)
                        .ok("OK")
                )
                .then(() => {
                    promise.reject();
                });
        }
    }

    activateUsers(uids: any, inviteIds: any, giveSeat: any): Promise<any> {
        const deferred = this.$q.defer();
        const count = uids.length + inviteIds.length;
        const license = count > 1 ? count + " Licenses" : "License";
        const action = giveSeat ? " Activated" : " Revoked";

        this.updateLicenses(uids, inviteIds, giveSeat).then(
            () => {
                this.toast.success(license + action);
                deferred.resolve();
            },
            (error) => {
                this.toast.error(error);
                deferred.reject();
            }
        );
        return deferred.promise;
    }

    deactivateUsers(
        users: User[],
        invites: Invite[],
        ev: any
    ): Promise<string> {
        const deferred = this.$q.defer();
        let signedInUser = this.getActiveUser();
        if (
            this.checkManager.checked_items.indexOf(
                signedInUser.uid.toString()
            ) > -1
        ) {
            this.toast.success("Cannot revoke your own license");
        } else if (
            this.checkManager.checked_items.indexOf(
                this.campaignDataProvider.activeCampaign.campaignowner
            ) > -1
        ) {
            this.toast.success("Cannot revoke account owner's license");
        } else {
            const userIds = users.map((user) => {
                return user.uid;
            });
            const inviteIds = invites.map((invite) => {
                return invite.campaigninviteid;
            });
            const count = userIds.length + inviteIds.length;
            const license = count > 1 ? `${count} licenses` : "license";

            if (count) {
                this.dialog
                    .delete({
                        targetEvent: ev,
                        skipHide: true,
                        locals: {
                            bodyText: `This action will prevent the ${
                                count > 1 ? "users" : "user"
                            } from using the app and dashboard`,
                            title: `Revoke ${license}?`,
                        },
                    })
                    .then(() => {
                        this.updateLicenses(userIds, inviteIds, false).then(
                            () => {
                                const message =
                                    count > 1
                                        ? count + " Licenses Revoked"
                                        : "License Revoked";
                                this.toast.success(message);
                                deferred.resolve(message);
                            },
                            (error) => {
                                this.toast.error(error);
                                deferred.reject(error);
                            }
                        );
                    });
            } else {
                const message =
                    count !== 1
                        ? "None of these users have a license"
                        : "This user doesn't have a license";
                this.dialog.showPreConfiguredDialog(
                    this.dialog
                        .confirm()
                        .textContent(message)
                        .targetEvent(ev)
                        .ok("OK")
                );
                deferred.reject(message);
            }
        }
        return deferred.promise;
    }

    resetPassword(email: string | number): Promise<User> {
        const deferred = this.$q.defer();

        this.apiUserService
            .resetPassword({ uemail: email })
            .then((response: object) => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject();
                }
            });

        return deferred.promise;
    }

    setFilteredUsersCount(count: number): void {
        this.count = count;
        this.checkManager.setTotalCount(this.count);
    }

    setUsersCount(count) {
        this.usersCount = count;
    }

    resetUsersCount() {
        this.usersCount = null;
        this.getUsersCount();
    }

    clearAll(): void {
        this.users = null;
        this.filteredUsers = [];
        this.searchPromise = null;
        this.count = null;
        this.usersCount = null;
        this.activeUser = null;
        this.filters = new Filter();
        this.statsPromise = null;
        this.stats = null;
        this.seatsPromise = null;
        this.seats = null;
    }

    userWithID(uid: string | number): User {
        for (let i = 0; i < this.users.length; i++) {
            if (this.users[i].uid == uid) {
                return this.users[i];
            }
        }
        return null;
    }

    getUser(uid: string | number): Promise<User> {
        const deferred = this.$q.defer();

        this.apiUserService.info({ id: uid }).then((response: object) => {
            let json = response["data"];
            if (json.success) {
                json.data.account_type = this.$cookies.get("account_type");
                deferred.resolve(this.modelsUtils.user(json.data));
            } else {
                deferred.reject();
            }
        });

        return deferred.promise;
    }

    setActiveUser(user: User): void {
        this.activeUser = user;
    }

    getActiveUser(): User {
        return this.activeUser;
    }

    spliceUser(user: User): void {
        //Remove team from list
        if (this.users !== null) {
            this.$timeout(() => {
                let index = this.users.indexOf(user);
                if (index > -1) {
                    this.users.splice(index, 1);
                }
            });
        }
    }

    removeUser(user: User): Promise<any> {
        const deferred = this.$q.defer();
        this.userService.remove({ id: user.uid }).then((response: object) => {
            let json = response["data"];
            if (json.success) {
                this.spliceUser(user);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    resetAllUsersTeams(): void {
        if (this.users) {
            for (let i = 0; i < this.users.length; i++) {
                this.users[i].resetTeams();
            }
        }
    }

    replaceUser(user: User): void {
        this.$timeout(() => {
            let existingUser = this.userWithID(user.uid);
            if (existingUser) {
                angular.extend(existingUser, user);
            } else {
                this.users.unshift(user);
            }
        });
    }

    processSocketUpdate(socketData: any): void {
        if (this.users) {
            this.replaceUser(this.modelsUtils.user(socketData));
        }
    }

    processSocketCreate(socketData: any): void {
        if (this.users) {
            this.replaceUser(this.modelsUtils.user(socketData));
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.users) {
            let existingUser = this.userWithID(socketData.uid);
            if (existingUser) {
                this.spliceUser(existingUser);
            }
        }
    }
}

angular.module("vcDataProvider").service("UserDataProvider", UserDataProvider);
